import type { LocalizationContext } from '@athena-lib/i18n';
import type { ProductWizardContext } from '../context/ProductWizardContext';
import { DeliverySheetSizeHelper } from './DeliverySheetSizeHelper';
import { TransformHelper } from './TransformHelper';

type MaxSize = number | string | null;

export class SizeHelper {
  public mm2pxFactor: number = 2.83465;
  public minSizeMM: number = 0;
  public maxSizeMM: number = 0;
  public maxSizeOtherSideMM: number = 0;
  public fixedSize: boolean = false;
  public autoFitToSizeConstraintsOnEvaluate: boolean = true;
  public meassureDisplayAbbr: string = 'cm';
  public deliverySheet: DeliverySheetSizeHelper;
  public transform: TransformHelper;

  public static get MAX_SIZE_MM_DIGITAL(): number {
    return 1250;
  }

  public static get MAX_SIZE_MM_LASER(): number {
    return 275;
  }

  public static get MIN_SIZE_MM_DEFAULT(): number {
    return 25;
  }

  // These sizes will be in either centimeters or inches
  public minSize: number = 0;
  public maxSize: number = 0;
  public maxSizeOtherSide: number = 0;

  constructor(protected context: ProductWizardContext) {
    this.deliverySheet = new DeliverySheetSizeHelper(context);
    this.transform = new TransformHelper(this);
  }

  public toString(): string {
    let value = '|';
    value += this.getMeasureAttrInMM('width') + '|';
    value += this.getMeasureAttrInMM('height') + '|';
    value += this.minSizeMM + '|';
    value += this.maxSizeMM + '|';
    value += this.fixedSize + '|';
    value += this.isImperialUnits() + '|';

    return value;
  }

  public evaluate(): void {
    // WARNING! This needs to be on pair with ProductItemEditorHelper.php
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////
    this.evaluateMaxSize();
    this.evaluateMinSize();
    this.evaluateMaxSizeOtherSide();
    this.evaluateFixedSize();
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////

    if (this.isImperialUnits()) {
      // Inches
      this.meassureDisplayAbbr = '"';
      this.minSize = this.convertMMToInches(this.minSizeMM);
      this.maxSize = this.convertMMToInches(this.maxSizeMM);
      this.maxSizeOtherSide = this.convertMMToInches(this.maxSizeOtherSideMM);
    } else {
      // Centimeters
      this.meassureDisplayAbbr = 'cm';
      this.minSize = this.convertMMToCM(this.minSizeMM);
      this.maxSize = this.convertMMToCM(this.maxSizeMM);
      this.maxSizeOtherSide = this.convertMMToCM(this.maxSizeOtherSideMM);
    }
  }

  /**
   * WARNING! This needs to be on pair with ProductItemEditorHelper.php
   */
  public evaluateFixedSize(): void {
    this.fixedSize = this.context.productItem.getAttrValue('fixed_size')
      ? (this.context.productItem.getAttrValue('fixed_size') as boolean)
      : this.context.attrComputer.isInFilteredValues('fixed_size', true);
  }

  /**
   * WARNING! This needs to be on pair with ProductItemEditorHelper.php
   */
  public evaluateMaxSize(): void {
    let maxSizeBasedOnProductionLine: number = this.canBeProductionLine('digital')
      ? SizeHelper.MAX_SIZE_MM_DIGITAL
      : SizeHelper.MAX_SIZE_MM_LASER;
    const maxSizeMM: MaxSize = this.context.productItem.getAttrValue('max_size_mm')
      ? this.context.productItem.getAttrValue('max_size_mm')
      : this.context.attrComputer.getHighestAvailableValue('max_size_mm');

    if (this.isIndependentOfMaxProdLineSize()) {
      maxSizeBasedOnProductionLine =
        this.context.attrComputer.getHighestAvailableValue('max_size_mm') ??
        Math.max(SizeHelper.MAX_SIZE_MM_DIGITAL, SizeHelper.MAX_SIZE_MM_LASER);
    }

    this.maxSizeMM = Math.min(Number(maxSizeMM) || Infinity, maxSizeBasedOnProductionLine);
  }

  public evaluateMinSize(): void {
    if (this.context.productItem.getAttrValue('min_size_mm')) {
      this.minSizeMM = Number(this.context.productItem.getAttrValue('min_size_mm'));
    } else {
      this.minSizeMM =
        this.context.attrComputer.getLowestAvailableValue('min_size_mm') ??
        SizeHelper.MIN_SIZE_MM_DEFAULT;
    }
  }

  /**
   * WARNING! This needs to be on pair with SizeHelper.php
   */
  public evaluateMaxSizeOtherSide(): void {
    let maxSizeOtherSide = this.getMaxSizeOtherSideValue();

    this.ensureMaxAndMinSizesEvaluated();

    if (!maxSizeOtherSide || maxSizeOtherSide > this.maxSizeMM) {
      maxSizeOtherSide = this.maxSizeMM;
    } else if (maxSizeOtherSide < this.minSizeMM) {
      maxSizeOtherSide = this.minSizeMM;
    }

    this.maxSizeOtherSideMM = maxSizeOtherSide;
  }

  private getMaxSizeOtherSideValue(): number | null {
    let value: number | string | null =
      this.context.productItem.getAttrValue('max_size_other_side_mm');

    if (!value) {
      const values = this.context.attrComputer.getFilteredValues('max_size_other_side_mm');

      if (values.length) {
        value = Number(values[0]);
      }
    }

    return value ? Number(value) : null;
  }

  private ensureMaxAndMinSizesEvaluated(): void {
    if (!this.maxSizeMM) {
      this.evaluateMaxSize();
    }

    if (!this.minSizeMM) {
      this.evaluateMinSize();
    }
  }

  public canBeProductionLine(productionLine: string): boolean {
    return !this.context.attrComputer.isConstrained('production_line', productionLine);
  }

  public formatDisplayableMeassure(measureInInchesOrCM: number): string {
    return measureInInchesOrCM.toString() + ' ' + this.meassureDisplayAbbr;
  }

  public formatMM(mm: number): number {
    mm = Math.round(mm * 10) / 10;
    mm = Number(mm.toFixed(0));

    return mm;
  }

  public convertMMToInches(mm: number): number {
    mm = this.formatMM(mm);
    const inches = mm / 25.4;
    const roundedInches = Math.round(inches * 10) / 10;

    return roundedInches;
  }

  public convertInchesToMM(inches: number): number {
    const mm = this.formatMM(inches * 25.4);

    return mm;
  }

  public convertMMToCM(mm: number): number {
    return Number((mm / 10).toFixed(1));
  }

  public convertCMToMM(cm: number): number {
    return Number((cm * 10).toFixed(0));
  }

  public setMeasureBySizeString(sizeString: string): void {
    if (sizeString) {
      const meassures = this.extractMeassuresFromString(sizeString);
      this.setMeasureAttr('width_mm', meassures.width);
      this.setMeasureAttr('height_mm', meassures.height);
    }
  }

  /**
   * Takes a size string like 2" x 2" or 10 x 10 cm and extract the numbers (but does NOT convert it to millimeters).
   *
   * @param sizeString
   */
  public extractMeassuresFromString(sizeString: string): { width: number; height: number } {
    const widthAndHeight: string[] = sizeString
      .replace('A5', '')
      .match(/([\d]{1,3}\.?[\d]{1,3})|([\d]{1,3})/g) as string[];
    const width: number = parseFloat(widthAndHeight[0]);
    const height: number = parseFloat(widthAndHeight[1]);

    return {
      width: width,
      height: height,
    };
  }

  public setMeasureAttr(attributeName: string, measureInInchesOrCM: number): number {
    attributeName = this.sanitizeMeasureAttrName(attributeName);
    const measureInMM = this.convertMeassureToMM(measureInInchesOrCM);

    return this.setMeasureAttrInMM(attributeName, measureInMM);
  }

  public setMeasureAttrInMM(attributeName: string, measureInMM: number): number {
    attributeName = this.sanitizeMeasureAttrName(attributeName);
    if (this.maxSizeMM && measureInMM > this.maxSizeMM) {
      measureInMM = this.maxSizeMM;
    } else if (this.minSizeMM && measureInMM < this.minSizeMM) {
      measureInMM = this.minSizeMM;
    }

    measureInMM = this.formatMM(measureInMM);

    this.context.productItem.setAttrValue(attributeName, measureInMM);

    return measureInMM;
  }

  public convertMeassureToMM(measureInInchesOrCM: number): number {
    let measureInMM: number;
    if (this.isImperialUnits()) {
      measureInMM = this.convertInchesToMM(measureInInchesOrCM);
    } else {
      measureInMM = this.convertCMToMM(measureInInchesOrCM);
    }

    return measureInMM;
  }

  public getMeasureAttrInMM(attributeName: string): number {
    attributeName = this.sanitizeMeasureAttrName(attributeName);
    return parseInt(this.context.productItem.getAttrValue(attributeName));
  }

  public getMeasureAttrInInches(attributeName: string): number {
    const mm: number = this.getMeasureAttrInMM(attributeName);

    return this.convertMMToInches(mm);
  }

  public getMeasureAttrInPixels(attributeName: string): number {
    const mm: number = this.getMeasureAttrInMM(attributeName);

    return mm * this.mm2pxFactor;
  }

  public getMeasureAttrDisplayable(attributeName: string): number {
    attributeName = this.sanitizeMeasureAttrName(attributeName);

    return this.convertMMToLocalizedMeassure(this.getMeasureAttrInMM(attributeName));
  }

  public convertMMToLocalizedMeassure(meassureInMM: number): number {
    let measure: number;

    if (this.isImperialUnits()) {
      measure = this.convertMMToInches(meassureInMM);
    } else {
      measure = this.convertMMToCM(meassureInMM);
    }

    return measure;
  }

  public sanitizeMeasureAttrName(attributeName: string) {
    if (attributeName.indexOf('_mm') < 0) {
      attributeName = attributeName + '_mm';
    }

    return attributeName;
  }

  get sqm(): number {
    const quantity: number = Number(this.context.productItem.getAttrValue('quantity'));
    return (
      (this.getMeasureAttrInMM('width') * this.getMeasureAttrInMM('height') * quantity) / 1000000
    );
  }

  get widthDisplayable(): number {
    return this.getMeasureAttrDisplayable('width_mm');
  }

  get heightDisplayable(): number {
    return this.getMeasureAttrDisplayable('height_mm');
  }

  get selectedProductionLine(): string | null {
    return this.context.productItem.getAttrValue('production_line');
  }

  public isImperialUnits(): boolean {
    const value = this.context.productItem.getAttrValue('imperial_units');
    return value === true || value === 'true';
  }

  public isSizeFixed(): boolean {
    return this.fixedSize;
  }

  public hasMaxSizeOtherSide(): boolean {
    return this.maxSizeOtherSideMM != this.maxSizeMM;
  }

  public isIndependentOfMaxProdLineSize(): boolean {
    if (this.context.productItem.productName == 'labels_on_roll') {
      return true;
    }

    return false;
  }

  public get maxSizeWidthMM(): number {
    if (
      this.hasMaxSizeOtherSide() &&
      this.getMeasureAttrInMM('width') < this.getMeasureAttrInMM('height')
    ) {
      return this.maxSizeOtherSideMM;
    }

    return this.maxSizeMM;
  }

  public get maxSizeHeightMM(): number {
    if (
      this.hasMaxSizeOtherSide() &&
      this.getMeasureAttrInMM('width') >= this.getMeasureAttrInMM('height')
    ) {
      return this.maxSizeOtherSideMM;
    }

    return this.maxSizeMM;
  }
}
export default function formatSize(
  width: string | number,
  height: string | number,
  locale: LocalizationContext,
  convert: boolean,
): string {
  const inchCountries = ['us'];
  const unit: 'cm' | 'inch' = inchCountries.includes(locale.countryCode) ? 'inch' : 'cm';
  const formatUnit = {
    width: unit === 'cm' ? '' : '"',
    height: unit === 'cm' ? ' cm' : '"',
  };

  const parsedWidth = Number.parseFloat(width as string);
  const parsedHeight = Number.parseFloat(height as string);
  const decimals = unit === 'inch' ? 1 : 2;

  const conversionFactor = unit == 'inch' ? 25.4 : 10;

  const widthFormatted = convert
    ? `${parseFloat((parsedWidth / conversionFactor).toFixed(decimals))}${formatUnit.width}`
    : `${parsedWidth}${formatUnit.width}`;
  const heightFormatted = convert
    ? `${parseFloat((parsedHeight / conversionFactor).toFixed(decimals))}${formatUnit.height}`
    : `${parsedHeight}${formatUnit.height}`;

  return `${widthFormatted} x ${heightFormatted}`;
}
